// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    const NOTIF_EVENT_CONFIG = {
      "admin.notification": { sound: true, toast: true, browser: true },
      "booking.created":    { sound: true, toast: true, browser: true },
      "booking.updated":    { sound: true, toast: false, browser: true },
      "patient.checkin.created": { sound: true, toast: true, browser: true },
      "patient.intake.created":  { sound: true, toast: true, browser: true },
      "lead.created":       { sound: true, toast: true, browser: true },
      "demo.requested":     { sound: true, toast: true, browser: true },
      "client.created":     { sound: true, toast: true, browser: true },
      "invoice.created":    { sound: true, toast: false, browser: false },
      "invoice.paid":       { sound: true, toast: true, browser: true },
      "payment.received":   { sound: true, toast: true, browser: true },
      "estimate.created":   { sound: true, toast: false, browser: false },
      "estimate.accepted":  { sound: true, toast: true, browser: true },
      "estimate.rejected":  { sound: true, toast: true, browser: true },
      "contract.created":   { sound: true, toast: false, browser: false },
      "contract.signed":    { sound: true, toast: true, browser: true },
      "contract.declined":  { sound: true, toast: true, browser: true },
    };

    function handleMessage(ev) {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      if (msg?.type === "pong") return;

      const { event, payload } = msg || {};
      if (!event || !payload) return;

      const cfg = NOTIF_EVENT_CONFIG[event];
      if (!cfg) return;

      if (cfg.sound) playNotificationSound();

      addFromWs({ ...payload, event });

      if (cfg.browser && Notification?.permission === "granted") {
        new Notification(payload.title || "CodeForce BMS", {
          body: payload.body || "",
        });
      }

      if (cfg.toast) {
        notification.info({
          message: payload.title || "New Notification",
          description: payload.body || "",
          duration: 5,
          placement: "topRight",
        });
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
